import React, { useState } from "react";
import { AlertCircle, Link as LinkIcon, Loader2 } from "lucide-react";
import { useGetAdminProjects } from "../../../lib/api/adminProjects";
import { AssignProjectPayload } from "../../../lib/types/adminProjects";

interface AssignProjectModalProps {
  userId: string;
  onClose: () => void;
  onAssign: (params: {
    userId: string;
    payload: AssignProjectPayload;
  }) => Promise<any>;
  isPending: boolean;
}

const AssignProjectModal: React.FC<AssignProjectModalProps> = ({
  userId,
  onClose,
  onAssign,
  isPending,
}) => {
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [verificationLink, setVerificationLink] = useState("");
  const [assignmentNote, setAssignmentNote] = useState("");
  const [error, setError] = useState("");

  const { data: projectsData, isLoading } = useGetAdminProjects({
    isActive: "true",
    limit: 100,
  });
  const projects = projectsData?.data?.projects || [];

  const handleSubmit = async () => {
    setError("");

    if (!selectedProject) {
      setError("Please select a project");
      return;
    }

    if (!verificationLink) {
      setError("Verification link is required");
      return;
    }

    try {
      new URL(verificationLink);
    } catch {
      setError("Please enter a valid URL");
      return;
    }

    try {
      await onAssign({
        userId,
        payload: {
          projectId: selectedProject,
          verificationLink,
          assignmentNote,
        },
      });
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Assign Project</h2>
          <p className="text-sm text-gray-500 mt-1">
            Select a project and provide a verification link for this user
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Project <span className="text-red-500">*</span>
            </label>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-[#8B52FF]" />
              </div>
            ) : (
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
              >
                <option value="">Choose a project...</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.title} - ${project.hourlyRate}/hr (
                    {project.totalSlots - project.filledSlots} slots available)
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Verification Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Link <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={verificationLink}
                onChange={(e) => setVerificationLink(e.target.value)}
                placeholder="https://verification-platform.com/user/abc123"
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF]"
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              This link will be shown to the user after they apply for the
              project
            </p>
          </div>

          {/* Assignment Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (Optional)
            </label>
            <textarea
              value={assignmentNote}
              onChange={(e) => setAssignmentNote(e.target.value)}
              placeholder="Any additional notes for this assignment..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] resize-none"
            />
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-6 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !selectedProject || !verificationLink}
            className="px-6 py-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Assign Project
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignProjectModal;
